import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  ReactNode,
} from "react";
import { useAuth } from "./AuthContext";
import api from "../services/API";

interface Permissao {
  permissao_id: number;
  modulo: string;
  pode_ver: boolean;
  pode_incluir: boolean;
  pode_editar: boolean;
  pode_excluir: boolean;
}

interface PermissaoContextType {
  permissoes: Permissao[];
  loading: boolean;
  temPermissao: (
    modulo: string,
    acao: "ver" | "incluir" | "editar" | "excluir",
  ) => boolean;
  podeVerModulo: (modulo: string) => boolean;
  recarregarPermissoes: () => Promise<void>;
}

const PermissaoContext = createContext<PermissaoContextType | undefined>(
  undefined,
);

export const usePermissao = () => {
  const context = useContext(PermissaoContext);
  if (context === undefined) {
    throw new Error(
      "usePermissao deve ser usado dentro de um PermissaoProvider",
    );
  }
  return context;
};

interface PermissaoProviderProps {
  children: ReactNode;
}

export const PermissaoProvider: React.FC<PermissaoProviderProps> = ({
  children,
}) => {
  const { user, isAuthenticated } = useAuth();
  const [permissoes, setPermissoes] = useState<Permissao[]>([]);
  const [loading, setLoading] = useState(false);
  const [permissoesCarregadas, setPermissoesCarregadas] = useState(false);

  // Map interno para lookup rápido de permissões (O(1) em vez de O(n))
  // Atualizado automaticamente quando permissoes mudam
  const permissoesMap = useMemo(() => {
    const map = new Map<string, Permissao>();
    permissoes.forEach((permissao) => {
      map.set(permissao.modulo, permissao);
    });
    return map;
  }, [permissoes]);

  // Memoizar carregarPermissoes - não incluir permissoesCarregadas nas dependências
  // O useEffect já gerencia quando chamar esta função
  const carregarPermissoes = useCallback(async () => {
    if (!user || !isAuthenticated) {
      return;
    }

    // Modo central: não carrega permissões (não tem banco operacional)
    if (user.unidade === "central") {
      setPermissoes([]);
      setPermissoesCarregadas(true);
      return;
    }

    // Portal externo: o parceiro não tem perfil de permissão. Concede sinteticamente
    // o módulo da transportadora (ver/incluir/excluir); `editar` fica false pois não
    // há confirmação manual de entrega (entrega é automática). O escopo (só os
    // próprios agendamentos) é aplicado pelo backend via CNPJ do token.
    if (user.tipo === "externo") {
      setPermissoes([
        {
          permissao_id: -1,
          modulo: "agendamento_entrega",
          pode_ver: true,
          pode_incluir: true,
          pode_editar: false,
          pode_excluir: true,
        },
      ]);
      setPermissoesCarregadas(true);
      return;
    }

    setLoading(true);
    try {
      const response = await api.get(`/usuario/${user.usuario_id}/permissoes`);
      setPermissoes(response.data);
      setPermissoesCarregadas(true);
    } catch (error) {
      setPermissoes([]);
      setPermissoesCarregadas(true); // Marcar como carregado mesmo em caso de erro para evitar loops
    } finally {
      setLoading(false);
    }
  }, [user, isAuthenticated]);

  // Memoizar recarregarPermissoes
  const recarregarPermissoes = useCallback(async () => {
    setPermissoesCarregadas(false);
    await carregarPermissoes();
  }, [carregarPermissoes]);

  // Memoizar temPermissao - usa Map para lookup O(1) em vez de find O(n)
  const temPermissao = useCallback(
    (
      modulo: string,
      acao: "ver" | "incluir" | "editar" | "excluir",
    ): boolean => {
      const permissao = permissoesMap.get(modulo);
      if (!permissao) return false;

      switch (acao) {
        case "ver":
          return permissao.pode_ver;
        case "incluir":
          return permissao.pode_incluir;
        case "editar":
          return permissao.pode_editar;
        case "excluir":
          return permissao.pode_excluir;
        default:
          return false;
      }
    },
    [permissoesMap],
  );

  // Memoizar podeVerModulo - usa Map para lookup O(1) em vez de find O(n)
  const podeVerModulo = useCallback(
    (modulo: string): boolean => {
      const permissao = permissoesMap.get(modulo);
      return permissao ? permissao.pode_ver : false;
    },
    [permissoesMap],
  );

  // Usar user?.usuario_id em vez de user inteiro para evitar recarregamentos desnecessários
  // quando apenas outras propriedades do user mudarem
  const userId = user?.usuario_id;

  // Ref para rastrear o último userId carregado e evitar carregamentos duplicados
  const ultimoUserIdCarregadoRef = React.useRef<number | undefined>(undefined);
  const permissoesCarregadasRef = React.useRef<boolean>(false);

  // Sincronizar ref com state
  React.useEffect(() => {
    permissoesCarregadasRef.current = permissoesCarregadas;
  }, [permissoesCarregadas]);

  useEffect(() => {
    if (isAuthenticated && user && userId) {
      // Se o userId mudou, resetar e carregar novamente
      if (ultimoUserIdCarregadoRef.current !== userId) {
        setPermissoesCarregadas(false);
        permissoesCarregadasRef.current = false;
        ultimoUserIdCarregadoRef.current = userId;
        carregarPermissoes();
      } else if (!permissoesCarregadasRef.current) {
        // Se é o mesmo usuário mas ainda não carregou, carregar
        carregarPermissoes();
      }
    } else if (!isAuthenticated) {
      // Limpar quando desautenticar
      setPermissoes([]);
      setPermissoesCarregadas(false);
      permissoesCarregadasRef.current = false;
      ultimoUserIdCarregadoRef.current = undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, userId]); // userId muda quando o usuário muda, evitando loops

  // Memoizar o value do Context para evitar re-renderizações desnecessárias
  // em todos os componentes que consomem o contexto
  const value = useMemo<PermissaoContextType>(
    () => ({
      permissoes,
      loading,
      temPermissao,
      podeVerModulo,
      recarregarPermissoes,
    }),
    [permissoes, loading, temPermissao, podeVerModulo, recarregarPermissoes],
  );

  return (
    <PermissaoContext.Provider value={value}>
      {children}
    </PermissaoContext.Provider>
  );
};
